import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useTu } from "@src/appV2/i18n/useTu";
import { useHomeHealthQualificationsForVisitType } from "@src/appV2/redesign/HomeHealth/hooks/useHomeHealthQualificationsForVisitType";
import { Text } from "@src/appV2/redesign/components/Text";

import {
  isHourlyPricingAllowed,
  PricingType,
  VISIT_TYPE_LABELS,
  VisitType,
  type WorkerRequirements,
} from "../../types";

export interface VisitDisciplineData {
  visitType: string;
  workerRequirement: string;
  pricingType: PricingType;
}

interface Props {
  data: VisitDisciplineData;
  onChange: (field: keyof VisitDisciplineData, value: string | PricingType) => void;
}

const inputSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  "& .MuiOutlinedInput-root": { borderRadius: 2 },
};

/** Visit type + discipline + per-visit/per-hour selection for a single visit. */
export function MobileVisitDisciplineFields({ data, onChange }: Props) {
  const { tu } = useTu("homeHealth");

  // Worker requirements are constrained by visit type (e.g. Admission allows only
  // RN/PT), preventing impossible combinations like a Caregiver admission visit.
  const allowedWorkerRequirements = useHomeHealthQualificationsForVisitType({
    visitType: data.visitType as VisitType,
  });

  // Hourly pricing is only valid for REGULAR non-PT visits.
  const allowHourly = isHourlyPricingAllowed({
    visitType: data.visitType as VisitType,
    workerReq: data.workerRequirement as WorkerRequirements,
  });

  // Some visit types are gated by feature flags on desktop, so mirror those
  // gates here. Evaluation is PT-only, so only offer it when the PT flag is on
  // (otherwise its PT-only professional list would let a workplace post a PT
  // visit without the flag). Admission & First Visit is likewise experimental
  // and only offered when its flag is on (matching desktop's CreateVisitForm).
  const isPtEnabled = useCbhFlag(CbhFeatureFlag.HOME_HEALTH_PT_ENABLED, {
    defaultValue: false,
  });
  const isAdmissionAndFirstVisitEnabled = useCbhFlag(
    CbhFeatureFlag.IS_ADMISSION_AND_FIRST_VISIT_ENABLED,
    { defaultValue: false }
  );
  const visitTypeOptions = Object.entries(VISIT_TYPE_LABELS).filter(([type]) => {
    if (type === VisitType.EVALUATION) {
      return isPtEnabled;
    }
    if (type === VisitType.ADMISSION_AND_FIRST_VISIT) {
      return isAdmissionAndFirstVisitEnabled;
    }
    return true;
  });

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Text semibold variant="body1">
          What kind of visit?
        </Text>
        <FormControl fullWidth sx={inputSx}>
          <Select
            displayEmpty
            value={data.visitType}
            onChange={(e) => {
              onChange("visitType", e.target.value);
              // Clear the discipline; it may not be valid for the new visit type.
              onChange("workerRequirement", "");
            }}
            renderValue={(value) =>
              value ? VISIT_TYPE_LABELS[value as VisitType] : "Select an option"
            }
          >
            {visitTypeOptions.map(([type, label]) => (
              <MenuItem key={type} value={type}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack spacing={1}>
        <Text semibold variant="body1">
          What kind of professional?
        </Text>
        <FormControl fullWidth sx={inputSx} disabled={!data.visitType}>
          <Select
            displayEmpty
            value={data.workerRequirement}
            onChange={(e) => {
              onChange("workerRequirement", e.target.value);
            }}
            renderValue={(value) =>
              value
                ? (value as string)
                : data.visitType
                  ? "Select an option"
                  : "Select a visit type first"
            }
          >
            {allowedWorkerRequirements.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {allowHourly && (
        <Stack spacing={0.5}>
          <Text semibold variant="body1">
            Type
          </Text>
          <Text variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
            {tu("Hourly visits must be expected to take at least 2 hours")}
          </Text>
          <ToggleButtonGroup
            value={data.pricingType}
            exclusive
            onChange={(_, newValue: PricingType | null) => {
              if (newValue !== null) {
                onChange("pricingType", newValue);
              }
            }}
            sx={{ mt: 1 }}
          >
            <ToggleButton value={PricingType.PER_VISIT}>{tu("Per visit")}</ToggleButton>
            <ToggleButton value={PricingType.PER_HOUR}>{tu("Per hour (2+ hrs)")}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
    </Stack>
  );
}
